import { Fragment } from "react";

// Renderizador mínimo para os manuais de ajuda (sem dependências): títulos #/##/###,
// parágrafos, listas (- e 1.), tabelas com pipes e **negrito**, *itálico*, texto em crases.
// Só gera elementos React (nunca HTML em bruto), por isso o conteúdo não pode injetar nada.

export const slugify = (s) =>
  s
    .toLowerCase()
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-|-$/g, "");

const INLINE = /(\*\*[^*]+\*\*|\*[^*\s][^*]*\*|`[^`]+`)/g;

function inline(text, keyBase) {
  return text.split(INLINE).map((part, i) => {
    const key = `${keyBase}-${i}`;
    if (part.startsWith("**") && part.endsWith("**") && part.length > 4) return <strong key={key}>{part.slice(2, -2)}</strong>;
    if (part.startsWith("`") && part.endsWith("`") && part.length > 2) return <code key={key} className="rounded bg-slate-800 px-1 py-0.5 text-[0.9em]">{part.slice(1, -1)}</code>;
    if (part.startsWith("*") && part.endsWith("*") && part.length > 2) return <em key={key}>{part.slice(1, -1)}</em>;
    return <Fragment key={key}>{part}</Fragment>;
  });
}

const splitRow = (line) => line.trim().replace(/^\|/, "").replace(/\|$/, "").split("|").map((c) => c.trim());

// Divide o markdown em blocos tipados.
export function parseBlocks(md) {
  const lines = md.replace(/\r\n/g, "\n").split("\n");
  const blocks = [];
  let i = 0;
  while (i < lines.length) {
    const line = lines[i];
    if (!line.trim()) { i += 1; continue; }

    const im = line.trim().match(/^!\[([^\]]*)\]\((\/[^)\s]+)\)$/);
    if (im) { blocks.push({ type: "img", alt: im[1], src: im[2] }); i += 1; continue; }

    const h = line.match(/^(#{1,3})\s+(.*)$/);
    if (h) { blocks.push({ type: "h", level: h[1].length, text: h[2] }); i += 1; continue; }

    if (line.trim().startsWith("|")) {
      const rows = [];
      while (i < lines.length && lines[i].trim().startsWith("|")) { rows.push(lines[i]); i += 1; }
      const cells = rows.map(splitRow);
      const hasSep = cells.length > 1 && cells[1].every((c) => /^:?-{2,}:?$/.test(c));
      blocks.push({ type: "table", head: cells[0], body: cells.slice(hasSep ? 2 : 1) });
      continue;
    }

    const ol = line.match(/^(\d+)\.\s+(.*)$/);
    if (ol) {
      const start = parseInt(ol[1], 10);
      const items = [];
      while (i < lines.length && /^\d+\.\s+/.test(lines[i])) { items.push(lines[i].replace(/^\d+\.\s+/, "")); i += 1; }
      blocks.push({ type: "ol", start, items });
      continue;
    }

    if (/^-\s+/.test(line)) {
      const items = [];
      while (i < lines.length && /^-\s+/.test(lines[i])) { items.push(lines[i].replace(/^-\s+/, "")); i += 1; }
      blocks.push({ type: "ul", items });
      continue;
    }

    const para = [];
    while (i < lines.length && lines[i].trim() && !/^(#{1,3}\s|\||\d+\.\s|-\s|!\[)/.test(lines[i])) { para.push(lines[i].trim()); i += 1; }
    blocks.push({ type: "p", text: para.join(" ") });
  }
  return blocks;
}

export default function MarkdownLite({ source }) {
  const blocks = parseBlocks(source);
  return (
    <div className="space-y-4 text-[15px] leading-relaxed text-slate-300">
      {blocks.map((b, idx) => {
        const k = `b${idx}`;
        if (b.type === "h") {
          if (b.level === 1) return null; // o título da página vem do cabeçalho
          if (b.level === 2) return <h2 key={k} id={slugify(b.text)} className="scroll-mt-20 pt-6 text-xl font-semibold text-white border-b border-slate-800 pb-2">{b.text}</h2>;
          return <h3 key={k} id={slugify(b.text)} className="scroll-mt-20 pt-2 text-base font-semibold text-slate-100">{b.text}</h3>;
        }
        // Shown at about half its pixel width (crisp on high-density screens); a click opens the full-size image.
        if (b.type === "img") return (
          <a key={k} href={b.src} target="_blank" rel="noreferrer" className="block">
            <img src={b.src} alt={b.alt} loading="lazy" className="mx-auto h-auto w-full max-w-md rounded-lg border border-slate-800" />
          </a>
        );
        if (b.type === "p") return <p key={k}>{inline(b.text, k)}</p>;
        if (b.type === "ul") return <ul key={k} className="list-disc space-y-1.5 pl-6 marker:text-slate-500">{b.items.map((t, j) => <li key={j}>{inline(t, `${k}-${j}`)}</li>)}</ul>;
        if (b.type === "ol") return <ol key={k} start={b.start} className="list-decimal space-y-1.5 pl-6 marker:text-slate-500">{b.items.map((t, j) => <li key={j}>{inline(t, `${k}-${j}`)}</li>)}</ol>;
        return (
          <div key={k} className="overflow-x-auto rounded-lg border border-slate-800">
            <table className="w-full text-sm">
              <thead className="bg-slate-800/60 text-left text-slate-200">
                <tr>{b.head.map((c, j) => <th key={j} className="px-3 py-2 font-semibold">{inline(c, `${k}h${j}`)}</th>)}</tr>
              </thead>
              <tbody>
                {b.body.map((row, r) => (
                  <tr key={r} className="border-t border-slate-800 align-top">
                    {row.map((c, j) => <td key={j} className="px-3 py-2">{inline(c, `${k}r${r}c${j}`)}</td>)}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        );
      })}
    </div>
  );
}
